import { MouseEvent, memo } from "react"
import { Handle, Position, useReactFlow, useStoreApi } from "@xyflow/react"
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen"
import {
  collapseGroup,
  expandGroup,
  recalculateParentGroupsSizes,
  sortNodes,
} from "@features/builder/graph/helpers"
import OpenInFullIcon from "@mui/icons-material/OpenInFull"
import { AppNode, GroupNode, NodeType } from "@features/builder/graph/types"
import NodeContainer from "@features/builder/graph/nodes/NodeContainer"
import { getRelativePosition, groupTitleHeight } from "@features/builder/helpers/nodes"
import css from "@features/builder/graph/nodes/NodeGroupResizable/NodeGroupResizable.module.css"
import { NodeResizeTop } from "@features/builder/graph/nodes/NodeGroupResizable/NodeResizeTop"
import { NodeResizeRight } from "@features/builder/graph/nodes/NodeGroupResizable/NodeResizeRight"
import { NodeResizeBottom } from "@features/builder/graph/nodes/NodeGroupResizable/NodeResizeBottom"
import { NodeResizeLeft } from "@features/builder/graph/nodes/NodeGroupResizable/NodeResizeLeft"
import {
  AddNodeMode,
  addNodeModeAtom,
  nextDrawerNodeIdAtom,
  useGraph,
} from "@features/builder/graph/graphAtoms"
import { createNodeInGroup } from "@utils/node"
import { useAtom, useSetAtom } from "jotai"

export const NodeGroupResizable = memo((node: GroupNode) => {
  const { getNodes, setNodes, screenToFlowPosition, getInternalNode } = useReactFlow<AppNode>()
  const store = useStoreApi<AppNode>()
  const [addNodeMode, setAddNodeMode] = useAtom(addNodeModeAtom)
  const setNextDrawerNodeId = useSetAtom(nextDrawerNodeIdAtom)
  const { isLocked } = useGraph()

  const handleCollapseExpandClick = (event: MouseEvent) => {
    event.stopPropagation()
    const allNodes = getNodes()
    const isCollapsed = node.data.metaGroup?.isCollapsed
    const res = isCollapsed ? expandGroup(node, allNodes) : collapseGroup(node, allNodes)

    setNodes(res)
  }

  const handleClick = (event: MouseEvent) => {
    if (isLocked) return

    const isAddingNodeActive = addNodeMode !== AddNodeMode.None
    if (!isAddingNodeActive) return

    const internalNode = getInternalNode(node.id)
    if (!internalNode) return

    const flowClickPosition = screenToFlowPosition({ x: event.clientX, y: event.clientY })
    const addGroup = addNodeMode === AddNodeMode.CursorGroup
    const newNodeType = addGroup ? NodeType.Group : NodeType.Empty

    let updatedNodes = getNodes()

    const newNodePosition = getRelativePosition(
      flowClickPosition,
      internalNode.internals.positionAbsolute,
    )
    const newNode = createNodeInGroup(updatedNodes, node, newNodeType, newNodePosition)
    updatedNodes = recalculateParentGroupsSizes(newNode, [...updatedNodes, newNode])

    setNodes(sortNodes(updatedNodes))
    setAddNodeMode(AddNodeMode.None)

    setTimeout(() => {
      store.getState().addSelectedNodes([newNode.id])
      setNextDrawerNodeId(newNode.id)
    })
  }

  if (node.data.metaGroup?.isCollapsed) {
    return (
      <NodeContainer node={node}>
        <div
          onClick={handleClick}
          className="graph-node-container"
          style={{ background: node.data.metaGroup.color }}
        >
          <OpenInFullIcon
            fontSize="small"
            className={css.collapseIcon}
            onClick={handleCollapseExpandClick}
          />
          <Handle type="target" position={Position.Top} isConnectable={false} />
          <div className="graph-node-label">{node.data.label}</div>
          <Handle type="source" position={Position.Bottom} isConnectable={false} />
        </div>
      </NodeContainer>
    )
  }

  return (
    <NodeContainer node={node} hideConnector>
      <div
        onClick={handleClick}
        style={{ height: "100%", background: node.data.metaGroup.color, borderRadius: "3px" }}
      >
        <CloseFullscreenIcon
          fontSize="small"
          className={css.collapseIcon}
          onClick={handleCollapseExpandClick}
        />
        {!isLocked && (
          <>
            <NodeResizeTop node={node} />
            <NodeResizeRight node={node} />
            <NodeResizeBottom node={node} />
            <NodeResizeLeft node={node} />
          </>
        )}
        <div
          style={{
            height: groupTitleHeight,
            display: "flex",
            alignItems: "baseline",
            justifyContent: "center",
          }}
        >
          {node.data.label}
        </div>
      </div>
    </NodeContainer>
  )
})
NodeGroupResizable.displayName = "NodeGroupResizable"
